import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const AddressContext = createContext();

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth(); // Get user and loading state from AuthContext
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Get customer phone number from user context
  const getCustomerPhone = () => {
    console.log('🔍 AddressContext - User object:', user);
    console.log('🔍 AddressContext - User phoneNumber:', user?.phoneNumber);
    
    // Try multiple ways to get the phone number
    const phoneNumber = user?.phoneNumber || 
                       user?.phone || 
                       localStorage.getItem('customerPhone') ||
                       localStorage.getItem('userPhone');
    
    console.log('🔍 AddressContext - Final phone number:', phoneNumber);
    return phoneNumber || null;
  };

  const fetchSavedAddresses = async () => {
    const customerPhone = getCustomerPhone();
    if (!customerPhone) return;

    setLoading(true);
    try {
      console.log('🔄 Fetching saved addresses for:', customerPhone);
              const q = query(
                collection(db, 'savedAddresses'),
                where('customerPhone', '==', customerPhone)
              );
      const querySnapshot = await getDocs(q);
      const addresses = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt (newest first) on client side
      addresses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return dateB - dateA;
      });
      
      setSavedAddresses(addresses);
      console.log('✅ Fetched saved addresses:', addresses.length);
    } catch (error) {
      console.error('❌ Error fetching saved addresses:', error);
      toast.error('Failed to load saved addresses');
    } finally {
      setLoading(false);
    }
  };

  // Fetch addresses when user changes (but wait for auth to load)
  useEffect(() => {
    if (!authLoading) { // Only fetch when auth is not loading
      fetchSavedAddresses();
    }
  }, [user, authLoading]);

  const saveAddress = async (addressData) => {
    const customerPhone = getCustomerPhone();
    if (!customerPhone) {
      if (authLoading) {
        toast.error('Please wait, authentication is loading...');
      } else if (!user) {
        toast.error('Please login first to save addresses');
      } else {
        toast.error('Phone number not found. Please login again.');
      }
      return false;
    }

    try {
      console.log('💾 Saving address:', addressData);
      const addressToSave = {
        ...addressData,
        customerPhone,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'savedAddresses'), addressToSave);
      console.log('✅ Address saved with ID:', docRef.id);
      
      // Add to local state
      setSavedAddresses(prev => [{
        id: docRef.id,
        ...addressToSave
      }, ...prev]);
      
      toast.success('Address saved successfully!');
      return true;
    } catch (error) {
      console.error('❌ Error saving address:', error);
      toast.error('Failed to save address');
      return false;
    }
  };

  const updateAddress = async (addressId, addressData) => {
    try {
      console.log('📝 Updating address:', addressId);
      const addressToUpdate = {
        ...addressData,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'savedAddresses', addressId), addressToUpdate);
      console.log('✅ Address updated successfully');
      
      // Update local state
      setSavedAddresses(prev => 
        prev.map(addr => 
          addr.id === addressId 
            ? { ...addr, ...addressToUpdate }
            : addr
        )
      );
      
      toast.success('Address updated successfully!');
      return true;
    } catch (error) {
      console.error('❌ Error updating address:', error);
      toast.error('Failed to update address');
      return false;
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      console.log('🗑️ Deleting address:', addressId);
      await deleteDoc(doc(db, 'savedAddresses', addressId));
      console.log('✅ Address deleted successfully');
      
      // Remove from local state
      setSavedAddresses(prev => prev.filter(addr => addr.id !== addressId));
      
      // Clear selected address if it was deleted
      if (selectedAddress?.id === addressId) {
        setSelectedAddress(null);
      }
      
      toast.success('Address deleted successfully!');
      return true;
    } catch (error) {
      console.error('❌ Error deleting address:', error);
      toast.error('Failed to delete address');
      return false;
    }
  };

  const selectAddress = (address) => {
    setSelectedAddress(address);
    console.log('📍 Address selected:', address);
  };

  const clearSelectedAddress = () => {
    setSelectedAddress(null);
    console.log('📍 Address selection cleared');
  };

  // Load addresses when component mounts
  useEffect(() => {
    fetchSavedAddresses();
  }, []);

  const value = {
    savedAddresses,
    selectedAddress,
    loading,
    fetchSavedAddresses,
    saveAddress,
    updateAddress,
    deleteAddress,
    selectAddress,
    clearSelectedAddress
  };

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
};
